/**
 * Implementation of all API calls to the backend. The services object is exported
 * for use in the other modules.
 *
 * For additional information on the fields required and return by the calls, check
 * documentation related to backend's views and serialisers.
 */
import axios from "axios";
import auth from "../auth/Auth.jsx";
import SockJS from "sockjs-client";
import Stomp from "stompjs";
import { resolveUploadContentType } from "../../components/DocumentHub/documentUploadMime.js";
/**
 * Verifies authentication state before attempting to use the backend API.
 *
 * In case an authentication refresh is required, the passed functions will
 * be queued and called when the refresh process completes, and false will
 * be returned. Otherwise, true is returned and no function is called.
 *
 * @param {function} service Service call to be queued
 * @param {function(boolean,object)} failCallback Called in case the refresh process fails
 * @returns {boolean}
 */
function _checkAuth(service, failCallback) {
  return auth.checkBeforeUse(function (result, error) {
    if (result) {
      if (service) {
        return service();
      }
    } else {
      if (failCallback) {
        failCallback(result, error);
      }
    }
  });
}

/**
 * Provides additional headers required for API interaction using axios.
 *
 * Currently, only the JWT authentication tokens need to be included as an HTTP
 * Authorization parameter.
 *
 * @returns {object}
 */
function _getHeaders() {
  return {
    Authorization: auth.accessToken.raw,
  };
}

/** Branch/Department override endpoint for a line item, by unified-view source. */
function _lineItemOverrideUrl(body, lineItemId, field) {
  const source = body && body.source ? String(body.source).toLowerCase() : "xero_invoice";
  if (source === "xero_bank" || source === "bank") {
    return "/api/v1/payments/bank-transactions/line-items/" + lineItemId + "/" + field;
  }
  if (source === "xero_expense_claim") {
    return "/api/v1/payments/expense-claims/line-items/" + lineItemId + "/" + field;
  }
  return "/api/v1/xeroinvoices/line-items/" + lineItemId + "/" + field;
}

/** Axios leaves body as string when Content-Type is not application/json. */
function _coerceJsonResponse(data) {
  if (typeof data !== "string") {
    return data;
  }
  const trimmed = data.trim();
  if (!trimmed || (trimmed.charAt(0) !== "{" && trimmed.charAt(0) !== "[")) {
    return data;
  }
  return JSON.parse(trimmed);
}

// calls is a map of type [String] CancelTokenStatic
// it is used to cancel an old HTTP Requests request when a new one comes, to the same endpoint and method (GET, POST, ...)
let calls = {};

/**
 * Closes an websocket and stomp client
 *
 * @param {object} mSock  the websocket object to be closed
 * @param {object} mStompClient  the stomp client object to be closed
 *
 * @return { void }
 */
export function closeWebsocket(mSock, mStompClient) {
  if (mSock) {
    try {
      mSock.close();
    } catch (err) {}
  }
  if (mStompClient) {
    try {
      mStompClient.disconnect(function () {});
    } catch (err) {}
  }
}

/**
 * Creates a new Websocket and Stomp client.
 *
 * @param { function () {} }  handleData is a callback function that process the data sent from the server
 * @param { string }          endpoint  the endpoint for which the stomp client is going to subscribe
 * @param { function (e){} }  onErrorCallback  function called when there is an error to the connection or the stomp client
 *
 * @return {object, object}   in case of sucess, this function returns a websocket object and a stomp client object, respectively, otherwise, it will return null for both
 */
export function createWebsocket(handleData, endpoint, onErrorCallback) {
  try {
    let finalBackendUrl = `${window.location.protocol}//${window.location.hostname}/secureWebsocket`;
    var sock = new SockJS(finalBackendUrl);
    var stompClient = Stomp.over(sock);
    stompClient.debug = () => {

    };

    let headers = {
      "X-Authorization": auth.accessToken.raw,
      "content-type": "application/json"

    };
    stompClient.connect(
      headers,
      function (frame) {
        stompClient.subscribe(
          endpoint,
          function (msg) {
            var msgBody = JSON.parse(msg.body);
            handleData(msgBody);
          },
          headers
        );
      },
      (err) => {
        if (onErrorCallback) {
          onErrorCallback(err);
        }
      }
    );
    return [sock, stompClient];
  } catch (err) {
    return [null, null];
  }
}

/**
 * Singleton that provides all the backend API calls as methods.
 */
export var services = {
  putProjectToStatus: function (body, projectID, statusID, callback) {
    if (
      !_checkAuth(
        () => services.putProjectToStatus(body, projectID, statusID, callback),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/projects/" + projectID + "/status/" + statusID;
    axios
      .put(url, body, {
        headers: _getHeaders(),
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  updateMasterRecurringEntry: function (projectID, recurringEntryID, callback) {
    if (
      !_checkAuth(
        () =>
          services.updateMasterRecurringEntry(
            projectID,
            recurringEntryID,
            callback
          ),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/projects/" + projectID + "/" + recurringEntryID + "/mre";
    axios
      .post(
        url,
        {},
        {
          headers: _getHeaders(),
        }
      )
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  postOnlineUsers: function (callback) {
    if (!_checkAuth(() => services.postOnlineUsers(callback), callback)) {
      return;
    }

    let url = "api/v1/onlineUsers";

    axios
      .post(
        url,
        {},
        {
          headers: _getHeaders(),
        }
      )
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },

  getUsersActivity: function (callback) {
    if (!_checkAuth(() => services.getUsersActivity(callback), callback)) {
      return;
    }

    axios
      .all([
        axios.get("api/v1/onlineUsers", {
          headers: _getHeaders(),
        }),
        axios.get("api/v1/activeUsers", {
          headers: _getHeaders(),
        }),
      ])
      .then(function (response) {
        callback(true, {
          onlineUsers: response[0].data,
          activeUsers: response[1].data,
        });
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  patchEmployeeFiscalYear: function (id, data, callback) {
    if (
      !_checkAuth(
        () => services.patchEmployeeFiscalYear(id, data, callback),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/employeeFiscalYear/" + id;

    axios
      .patch(url, data, {
        headers: _getHeaders(),
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  patchFiscalYearTeamSalesTarget: function (fiscalYearID, teamSalesTarget, callback) {
    if (
      !_checkAuth(
        () => services.patchFiscalYearTeamSalesTarget(fiscalYearID, teamSalesTarget, callback),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/fiscalYears/" + fiscalYearID + "/teamSalesTarget";

    axios
      .patch(url, { teamSalesTarget: teamSalesTarget }, {
        headers: _getHeaders(),
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  deleteMonthlyProfitLossByID: function (id, callback) {
    if (
      !_checkAuth(
        () => services.deleteMonthlyProfitLossByID(id, callback),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/monthlyProfitLoss/" + id;

    axios
      .delete(url, {
        headers: _getHeaders(),
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  patchMonthlyProfitLoss: function (id, data, callback) {
    if (
      !_checkAuth(
        () => services.patchMonthlyProfitLoss(id, data, callback),
        callback
      )
    ) {
      return;
    }
    let url = "api/v1/monthlyProfitLoss/" + id;

    axios
      .patch(url, data, {
        headers: _getHeaders(),
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  updateMonthlyProfitLoss: function (id, data, callback) {
    if (
      !_checkAuth(
        () => services.updateMonthlyProfitLoss(id, data, callback),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/monthlyProfitLoss/" + id;

    axios
      .put(url, data, {
        headers: _getHeaders(),
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  patchMonthlyProfitLossWithParams: function (params, data, callback) {
    if (
      !_checkAuth(
        () => services.patchMonthlyProfitLossWithParams(params, data, callback),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/monthlyProfitLoss";

    axios
      .patch(url, data, {
        headers: _getHeaders(),
        params,
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  createMonthlyProfitLoss: function (data, callback) {
    if (
      !_checkAuth(
        () => services.createMonthlyProfitLoss(data, callback),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/monthlyProfitLoss";

    axios
      .post(url, data, {
        headers: _getHeaders(),
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getMonthlyProfitLossByID: function (elementID, callback) {
    if (
      !_checkAuth(
        () => services.getMonthlyProfitLossByID(elementID, callback),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/monthlyProfitLoss/" + elementID;

    axios
      .get(url, {
        headers: _getHeaders(),
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getMonthlyProfitLoss: function (params, headers, callback) {
    if (
      !_checkAuth(
        () => services.getMonthlyProfitLoss(params, headers, callback),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/monthlyProfitLoss";

    axios
      .get(url, {
        headers: { ..._getHeaders(), ...headers },
        params: params,
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getMonthlyProfitLoss365Mesh: function (params, headers, callback) {
    if (
      !_checkAuth(
        () => services.getMonthlyProfitLoss365Mesh(params, headers, callback),
        callback
      )
    ) {
      return;
    }
    axios
      .get("api/v1/monthlyProfitLoss365Mesh", {
        headers: { ..._getHeaders(), ...(headers || {}) },
        params: params
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getMonthlyProfitLoss365MeshByID: function (id, callback) {
    if (
      !_checkAuth(
        () => services.getMonthlyProfitLoss365MeshByID(id, callback),
        callback
      )
    ) {
      return;
    }
    axios
      .get("api/v1/monthlyProfitLoss365Mesh/" + id, {
        headers: _getHeaders()
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  patchMonthlyProfitLoss365Mesh: function (id, data, callback) {
    if (
      !_checkAuth(
        () => services.patchMonthlyProfitLoss365Mesh(id, data, callback),
        callback
      )
    ) {
      return;
    }
    axios
      .patch("api/v1/monthlyProfitLoss365Mesh/" + id, data, {
        headers: _getHeaders()
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  putAdvancedSearchFilter: function (employeeID, data, callback) {
    if (
      !_checkAuth(
        () => services.putAdvancedSearchFilter(employeeID, data, callback),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/employees/" + employeeID + "/advancedSearch";
    axios
      .put(url, data, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },
  deleteEmployeeAdvancedSearch: function (
    employeeID,
    advancedSearchFilterID,
    callback
  ) {
    if (
      !_checkAuth(
        () =>
          services.deleteEmployeeAdvancedSearch(
            employeeID,
            advancedSearchFilterID,
            callback
          ),
        callback
      )
    ) {
      return;
    }

    let url =
      "api/v1/employees/" +
      employeeID +
      "/advancedSearch/" +
      advancedSearchFilterID;
    axios
      .delete(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },
  getEmployeeAdvancedSearch: function (employeeID, callback) {
    if (
      !_checkAuth(
        () => services.getEmployeeAdvancedSearch(employeeID, callback),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/employees/" + employeeID + "/advancedSearch";
    axios
      .get(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },
  putFiscalYears: function (fiscalYearID, data, callback) {
    if (
      !_checkAuth(
        () => services.putVendors(fiscalYearID, data, callback),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/fiscalYears/" + fiscalYearID.toString();

    axios
      .put(url, data, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },
  postFiscalYears: function (data, callback) {
    if (!_checkAuth(() => services.postFiscalYears(data, callback), callback)) {
      return;
    }

    let url = "api/v1/fiscalYears";

    axios
      .post(url, data, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getFiscalYear: function (fiscalYearID, callback) {
    if (
      !_checkAuth(
        () => services.getFiscalYear(fiscalYearID, callback),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/fiscalYears/" + fiscalYearID.toString();

    axios
      .get(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  deleteFiscalYear: function (fiscalYearID, callback) {
    if (
      !_checkAuth(
        () => services.deleteFiscalYear(fiscalYearID, callback),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/fiscalYears/" + fiscalYearID.toString();

    axios
      .delete(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  cloneProject: function (projectID, documentUuids, callback) {
    if (
      !_checkAuth(
        () => services.cloneProject(projectID, documentUuids, callback),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/projects/" + projectID + "/clone";

    axios
      .post(
        url,
        { documentUuids: documentUuids || [] },
        {
          headers: _getHeaders(),
        }
      )
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },
  createChild: function (projectID, callback) {
    if (
      !_checkAuth(() => services.createChild(projectID, callback), callback)
    ) {
      return;
    }

    let url = "api/v1/projects/" + projectID + "/child";

    axios
      .post(
        url,
        {},
        {
          headers: _getHeaders(),
        }
      )
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },
  getProjectGroupSummary: function (projectID, callback) {
    if (
      !_checkAuth(
        () => services.getProjectGroupSummary(projectID, callback),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/projects/" + projectID + "/groupSummary";

    axios
      .get(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        if (response.status === 204 || !response.data) {
          callback(true, null);
          return;
        }
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },
  getProjectGroupTypeBreakdown: function (projectID, callback) {
    if (
      !_checkAuth(
        () => services.getProjectGroupTypeBreakdown(projectID, callback),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/projects/" + projectID + "/groupTypeBreakdown";

    axios
      .get(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        if (response.status === 204 || !response.data) {
          callback(true, null);
          return;
        }
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },
  createMreProject: function (projectID, callback) {
    if (
      !_checkAuth(
        () => services.createMreProject(projectID, callback),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/projects/" + projectID + "/mre";

    axios
      .post(
        url,
        {},
        {
          headers: _getHeaders(),
        }
      )
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },
  getMsNewRevenue: function (params, callback) {
    if (
      !_checkAuth(() => services.getMsNewRevenue(params, callback), callback)
    ) {
      return;
    }

    axios
      .get("/api/v1/reporting/msNewRevenue", {
        headers: _getHeaders(),
        params: {
          from: params.from,
          to: params.to,
          fiscalYears: params.fiscalYears,
        },
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  updateMsExistingRevenue: function (projectId, existingRevenue, callback) {
    if (
      !_checkAuth(
        () => services.updateMsExistingRevenue(projectId, existingRevenue, callback),
        callback
      )
    ) {
      return;
    }

    axios
      .put(
        "/api/v1/reporting/msNewRevenue/" + projectId + "/existingRevenue",
        { existingRevenue: existingRevenue },
        { headers: _getHeaders() }
      )
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getEngineerProfitability: function (params, callback) {
    if (
      !_checkAuth(
        () => services.getEngineerProfitability(params, callback),
        callback
      )
    ) {
      return;
    }

    let url = "/api/v1/reporting/engineerProfitability";

    axios
      .get(url, {
        headers: _getHeaders(),
        params: {
          ...params,
        },
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getAccountManagerProfitability: function (params, callback) {
    if (
      !_checkAuth(
        () => services.getAccountManagerProfitability(params, callback),
        callback
      )
    ) {
      return;
    }

    let url = "/api/v1/reporting/accountManagerProfitability";

    axios
      .get(url, {
        headers: _getHeaders(),
        params: {
          ...params,
        },
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getCustomerRatingAndPriority: function (params, callback) {
    if (
      !_checkAuth(
        () => services.getCustomerRatingAndPriority(params, callback),
        callback
      )
    ) {
      return;
    }

    let url = "/api/v1/reporting/customerRatingAndPriority";

    axios
      .get(url, {
        headers: _getHeaders(),
        params: {
          ...params,
        },
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getNextCustomerNumber: function (callback) {
    if (!_checkAuth(() => services.getNextCustomerNumber(callback), callback)) {
      return;
    }

    let url = "/api/v1/clients/nextCustomerNumber";

    axios
      .get(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getAvailableProjectNumber: function (callback, options) {
    if (
      !_checkAuth(
        () => services.getAvailableProjectNumber(callback, options),
        callback
      )
    ) {
      return;
    }
    options = options || {};
    let params = {};
    if (!options.forceNew) {
      let availableProjectNumber = localStorage.getItem("availableProjectNumber");
      if (availableProjectNumber) {
        params["availableProjectNumber"] = availableProjectNumber;
      }
    }

    let url = "/api/v1/projects/availableProjectNumber";

    axios
      .get(url, {
        headers: _getHeaders(),
        params,
      })
      .then(function (response) {
        localStorage.setItem(
          "availableProjectNumber",
          response.data["projectNumber"]
        );
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  patchCommission: function (commissionID, commissionData, params, callback) {
    if (
      !_checkAuth(
        () =>
          services.patchCommission(
            commissionID,
            commissionData,
            params,
            callback
          ),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/commissions/" + commissionID.toString();

    axios
      .patch(url, commissionData, {
        headers: _getHeaders(),
        params: { ...params },
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },
  patchCommissionsBulk: function (payload, callback) {
    if (
      !_checkAuth(
        () =>
          services.patchCommissionsBulk(
            payload,
            callback
          ),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/commissions/bulk";

    axios
      .patch(url, payload, {
        headers: _getHeaders()
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },
  deleteTenderLoginByID: function (tenderLoginID, callback) {
    if (
      !_checkAuth(
        () => services.deleteTenderLoginByID(tenderLoginID, callback),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/tenderLogins/" + tenderLoginID.toString();

    axios
      .delete(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  postTenderLogins: function (data, callback) {
    if (!_checkAuth(() => services.getTenderLogins(data, callback), callback)) {
      return;
    }

    let url = "api/v1/tenderLogins";

    axios
      .post(url, data, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  putTenderLogins: function (tenderLoginID, data, callback) {
    if (
      !_checkAuth(
        () => services.getTenderLogins(tenderLoginID, data, callback),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/tenderLogins/" + tenderLoginID.toString();

    axios
      .put(url, data, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getTenderLogins: function (callback) {
    if (!_checkAuth(() => services.getTenderLogins(callback), callback)) {
      return;
    }

    let url = "api/v1/tenderLogins";

    axios
      .get(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getTenderLoginByID: function (id, callback) {
    if (
      !_checkAuth(() => services.getTenderLoginByUuid(id, callback), callback)
    ) {
      return;
    }

    let url = "api/v1/tenderLogins/" + id.toString();

    axios
      .get(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getTenderLoginByUuid: function (uuid, callback) {
    if (
      !_checkAuth(() => services.getTenderLoginByUuid(uuid, callback), callback)
    ) {
      return;
    }

    let url = "api/v1/tenderLogins/uuid/" + uuid;

    axios
      .get(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  // putSuppliersPromise: function(supplierID, data, callback) {
  //   if (
  //     !_checkAuth(
  //       () => services.putSuppliersPromise(supplierID, data, callback),
  //       callback
  //     )
  //   ) {
  //     return Promise.resolve({});
  //   }

  //   let url = "api/v1/suppliers/" + supplierID.toString();

  //   return axios.put(url, data, {
  //     headers: _getHeaders(),
  //     params: {}
  //   });
  // },
  putSuppliers: function (supplierID, data, callback) {
    if (
      !_checkAuth(
        () => services.putSuppliers(supplierID, data, callback),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/suppliers/" + supplierID.toString();

    axios
      .put(url, data, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },
  postSuppliers: function (data, callback) {
    if (!_checkAuth(() => services.postSuppliers(data, callback), callback)) {
      return;
    }

    let url = "api/v1/suppliers";

    axios
      .post(url, data, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getSupplier: function (supplierID, callback) {
    if (
      !_checkAuth(() => services.getSupplier(supplierID, callback), callback)
    ) {
      return;
    }

    let url = "api/v1/suppliers/" + supplierID.toString();

    axios
      .get(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  deleteSupplier: function (supplierID, callback) {
    if (
      !_checkAuth(() => services.deleteSupplier(supplierID, callback), callback)
    ) {
      return;
    }

    let url = "api/v1/suppliers/" + supplierID.toString();

    axios
      .delete(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getSuppliers: function (callback) {
    if (!_checkAuth(() => services.getSuppliers(callback), callback)) {
      return;
    }

    let url = "api/v1/suppliers";

    axios
      .get(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getNsaTotals: function (headers, params, responseType, callback) {
    if (
      !_checkAuth(
        () => services.getNsaTotals(headers, params, responseType, callback),
        callback
      )
    ) {
      return;
    }

    axios
      .get("/api/v1/nsa/totals", {
        headers: { ..._getHeaders(), ...headers },
        params: params,
        responseType: responseType,
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getNsaDashboardLists: function (params, callback) {
    if (
      !_checkAuth(
        () => services.getNsaDashboardLists(params, callback),
        callback
      )
    ) {
      return;
    }

    let employeesParams = {};
    if (params.employeesParams) {
      employeesParams = {
        ...params.employeesParams,
      };
    } else {
      employeesParams["role"] = "account_manager";
      employeesParams["sort"] = "email";
    }
    axios
      .all([
        axios.get("api/v1/fiscalYears", {
          headers: _getHeaders(),
          params: {},
        }),
        axios.get("api/v1/employees/directory", {
          headers: _getHeaders(),
          params: employeesParams,
        }),
        axios.get("api/v1/regions", {
          headers: _getHeaders(),
          params: {},
        }),
        axios.get("api/v1/projectTypes", {
          headers: _getHeaders(),
          params: {},
        }),
        axios.get("api/v1/technologies", {
          headers: _getHeaders(),
          params: {},
        }),
        axios.get("api/v1/nsa/totals", {
          headers: _getHeaders(),
          params: {},
        }),
        axios.get("api/v1/vendors", {
          headers: _getHeaders(),
          params: {},
        }),
        axios.get("api/v1/clients", {
          headers: _getHeaders(),
          params: {"short": 1},
        }),
      ])
      .then(function (response) {
        callback(true, response);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getUpdateQuoteData: function (quoteID, callback) {
    if (
      !_checkAuth(
        () => services.getCreateQuoteData(quoteID, callback),
        callback
      )
    ) {
      return;
    }

    axios
      .all([
        axios.get("/api/v1/quotes/" + quoteID, {
          headers: _getHeaders(),
          params: {},
        }),
        axios.get("/api/v1/internalSystem/lists", {
          headers: _getHeaders(),
          params: {},
        }),
      ])
      .then(function (response) {
        callback(true, response);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getRenewalsMainPageData: function (callback) {
    if (
      !_checkAuth(() => services.getRenewalsMainPageData(callback), callback)
    ) {
      return;
    }

    axios
      .all([
        axios.get("/api/v1/internalSystem/lists", {
          headers: _getHeaders(),
          params: {},
        }),
      ])
      .then(function (response) {
        callback(true, response);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  processExcel: function (formdata, callback) {
    if (
      !_checkAuth(
        () => services.processExcel(formdata, callback),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/quotes/processExcelQuote";

    axios
      .post(url, formdata, {
        headers: { ..._getHeaders(), "content-type": "multipart/form-data" },
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  parseQuoteWithAI: function (formdata, callback) {
    if (
      !_checkAuth(
        () => services.parseQuoteWithAI(formdata, callback),
        callback
      )
    ) {
      return;
    }
    let url = "api/v1/quotes/parseQuoteWithAI";
    axios
      .post(url, formdata, {
        headers: { ..._getHeaders(), "content-type": "multipart/form-data" },
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getFieldSourceMappings: function (callback) {
    if (
      !_checkAuth(
        () => services.getFieldSourceMappings(callback),
        callback
      )
    ) {
      return;
    }
    axios
      .get("api/v1/quotes/fieldSourceMappings", { headers: _getHeaders() })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  acceptQuoteMapping: function (mapping, callback) {
    if (
      !_checkAuth(
        () => services.acceptQuoteMapping(mapping, callback),
        callback
      )
    ) {
      return;
    }
    axios
      .post("api/v1/quotes/fieldSourceMappings/accept", mapping, {
        headers: _getHeaders(),
      })
      .then(function () {
        callback(true);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getCreateQuoteData: function (callback) {
    if (!_checkAuth(() => services.getCreateQuoteData(callback), callback)) {
      return;
    }

    let availableProjectNumber = localStorage.getItem("availableProjectNumber");
    let params = {};
    if (availableProjectNumber) {
      params["availableProjectNumber"] = availableProjectNumber;
    }
    axios
      .all([
        axios.get("/api/v1/projects/availableProjectNumber", {
          headers: _getHeaders(),
          params,
        }),
        axios.get("/api/v1/internalSystem/lists", {
          headers: _getHeaders(),
          params: {},
        }),
      ])
      .then(function (response) {
        localStorage.setItem(
          "availableProjectNumber",
          response[0].data["projectNumber"]
        );
        callback(true, response);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  // Expenses v1/v2 API clients (v2 UI at #/expensesv2; Payments at #/payments).
  getExpensesData: function (callback) {
    if (!_checkAuth(() => services.getExpensesData(callback), callback)) {
      return;
    }

    axios
      .all([
        axios.get("/api/v1/fiscalYears", {
          headers: _getHeaders(),
          params: {},
        }),
        axios.get("/api/v1/expenses", {
          headers: _getHeaders(),
          params: { fiscalYear: "current" },
        }),
      ])
      .then(function (response) {
        callback(true, response);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  listExpenses: function (params, callback) {
    if (!_checkAuth(() => services.listExpenses(params, callback), callback)) {
      return;
    }

    axios
      .get("/api/v1/expenses", {
        headers: _getHeaders(),
        params,
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getExpensesByRegionId: function (regionId,fyId,callback) {
    if (!_checkAuth(() => services.getExpensesByRegionId(regionId,fyId,callback), callback)) {
      return;
    }

    let url = "/api/v1/expenses/";
    if(fyId){
     url= url+ regionId+"/"+fyId;
    } else{
      url=url + regionId
    }

    axios
      .get(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getExpensesByFyId: function (fyId,callback) {
    if (!_checkAuth(() => services.getExpensesByFyId(fyId,callback), callback)) {
      return;
    }
    let url = "/api/v1/expenses/regions/totals/"+fyId;
    axios
      .get(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getPayments: function (params, callback) {
    if (!_checkAuth(() => services.getPayments(params, callback), callback)) {
      return;
    }
    let url = "/api/v1/payments/filter";
    axios
      .get(url, {
        headers: _getHeaders(),
        params: params || {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  // Distinct contact/customer name suggestions for the payments-module filter autosuggest.
  // `search` should be at least 3 characters; returns an array of name strings.
  getContactNames: function (search, callback) {
    if (!_checkAuth(() => services.getContactNames(search, callback), callback)) {
      return;
    }
    let url = "/api/v1/payments/contactNames";
    axios
      .get(url, {
        headers: _getHeaders(),
        params: { search: search || "" },
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getPaymentsAnalytics: function (params, callback) {
    if (!_checkAuth(() => services.getPaymentsAnalytics(params, callback), callback)) {
      return;
    }
    let url = "/api/v1/payments/analytics";
    axios
      .get(url, {
        headers: _getHeaders(),
        params: params || {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getBankTransactions: function (params, callback) {
    if (!_checkAuth(() => services.getBankTransactions(params, callback), callback)) {
      return;
    }
    let url = "/api/v1/payments/bank-transactions/filter";
    axios
      .get(url, {
        headers: _getHeaders(),
        params: params || {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getBankTransactionsAnalytics: function (params, callback) {
    if (!_checkAuth(() => services.getBankTransactionsAnalytics(params, callback), callback)) {
      return;
    }
    let url = "/api/v1/payments/bank-transactions/analytics";
    axios
      .get(url, {
        headers: _getHeaders(),
        params: params || {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  syncBankTransactionsFromXero: function (businessUnit, startDate, endDate, callback) {
    if (!_checkAuth(() => services.syncBankTransactionsFromXero(businessUnit, startDate, endDate, callback), callback)) {
      return;
    }
    const params = { businessUnit: businessUnit };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    axios
      .post("/api/v1/payments/bank-transactions/sync", null, {
        headers: _getHeaders(),
        params: params,
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  postExpenseType: function (data,callback) {
    if (!_checkAuth(() => services.postExpenseType(data,callback), callback)) {
      return;
    }
    let url = "/api/v1/expense/types";
    axios
      .post( url,data, {
          headers: _getHeaders(),
        }
      )
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  patchQuoteELements: function (data, callback) {
    if (
      !_checkAuth(() => services.patchQuoteELements(data, callback), callback)
    ) {
      return;
    }

    let url = "/api/v1/quoteElements";
    axios
      .patch(url, data, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  patchQuoteELementByID: function (id, data, callback) {
    if (
      !_checkAuth(
        () => services.patchQuoteELementByID(id, data, callback),
        callback
      )
    ) {
      return;
    }

    let url = "/api/v1/quoteElements/" + id;
    axios
      .patch(url, data, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  updateExpense: function (expenseID, data, callback) {
    if (
      !_checkAuth(
        () => services.updateExpense(expenseID, data, callback),
        callback
      )
    ) {
      return;
    }

    let url = "/api/v1/expenses/" + expenseID;
    axios
      .put(url, data, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  updateExpenseV2: function (data, callback) {
    if (
      !_checkAuth(
        () => services.updateExpense( data, callback),
        callback
      )
    ) {
      return;
    }

    let url = "/api/v1/expenses/";
    axios
      .put(url, data, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getNonQuotedOpportunitiesLists: function (callback) {
    if (
      !_checkAuth(
        () => services.getNonQuotedOpportunitiesLists(callback),
        callback
      )
    ) {
      return;
    }

    axios
      .all([
        axios.get("api/v1/clients", {
          headers: _getHeaders(),
          params: {},
        }),

        axios.get("api/v1/vendors", {
          headers: _getHeaders(),
          params: {},
        }),
        axios.get("api/v1/technologies", {
          headers: _getHeaders(),
          params: {},
        }),
      ])
      .then(function (response) {
        callback(true, response);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  putVendors: function (vendorID, data, callback) {
    if (
      !_checkAuth(() => services.putVendors(vendorID, data, callback), callback)
    ) {
      return;
    }

    let url = "api/v1/vendors/" + vendorID.toString();

    axios
      .put(url, data, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },
  postVendors: function (data, callback) {
    if (!_checkAuth(() => services.postVendors(data, callback), callback)) {
      return;
    }

    let url = "api/v1/vendors";

    axios
      .post(url, data, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getVendor: function (vendorID, callback) {
    if (!_checkAuth(() => services.getVendor(vendorID, callback), callback)) {
      return;
    }

    let url = "api/v1/vendors/" + vendorID.toString();

    axios
      .get(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  deleteVendor: function (vendorID, callback) {
    if (
      !_checkAuth(() => services.deleteVendor(vendorID, callback), callback)
    ) {
      return;
    }

    let url = "api/v1/vendors/" + vendorID.toString();

    axios
      .delete(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getVendorsParams: function (params, callback) {
    if (
      !_checkAuth(() => services.getVendorsParams(params, callback), callback)
    ) {
      return;
    }

    let url = "api/v1/vendors";

    axios
      .get(url, {
        headers: _getHeaders(),
        params: params,
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getVendors: function (callback) {
    if (!_checkAuth(() => services.getVendors(callback), callback)) {
      return;
    }

    let url = "api/v1/vendors";

    axios
      .get(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  deleteCustomerDiscount: function (customerDiscountID, callback) {
    if (
      !_checkAuth(
        () => services.deleteCustomerDiscount(customerDiscountID, callback),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/customerDiscounts/" + customerDiscountID.toString();

    axios
      .delete(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  putCustomerDiscount: function (customerDiscountID, data, callback) {
    if (
      !_checkAuth(
        () => services.putCustomerDiscount(customerDiscountID, data, callback),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/customerDiscounts/" + customerDiscountID.toString();

    axios
      .put(url, data, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  postCustomerDiscount: function (data, callback) {
    if (
      !_checkAuth(() => services.getCustomerDiscounts(data, callback), callback)
    ) {
      return;
    }

    let url = "api/v1/customerDiscounts";

    axios
      .post(url, data, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getCustomerDiscount: function (customerDiscountID, callback) {
    if (
      !_checkAuth(
        () => services.getCustomerDiscount(customerDiscountID, callback),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/customerDiscounts/" + customerDiscountID.toString();

    axios
      .get(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getCustomerDiscounts: function (callback) {
    if (!_checkAuth(() => services.getCustomerDiscounts(callback), callback)) {
      return;
    }

    let url = "api/v1/customerDiscounts";

    axios
      .get(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getCommissionAchieved: function (employeeID, params, callback) {
    if (
      !_checkAuth(
        () => services.getCommissionAchieved(employeeID, params, callback),
        callback
      )
    ) {
      return;
    }

    axios
      .all([
        axios.get("api/v1/employees/" + employeeID + "/commissionAchieved", {
          headers: _getHeaders(),
          params: { ...params },
        }),
      ])

      .then(function (response) {
        callback(true, response);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getCommissionsByProject: function (params, callback) {
    if (
      !_checkAuth(
        () => services.getCommissionsByProject(params, callback),
        callback
      )
    ) {
      return;
    }

    axios
      .get("api/v1/commissionsByProject", {
        headers: _getHeaders(),
        params: { ...params },
      })
      .then(function (response) {
        callback(true, response);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  markPaidFromCommsByProjectUpload: function (payload, callback) {
    if (
      !_checkAuth(
        () => services.markPaidFromCommsByProjectUpload(payload, callback),
        callback
      )
    ) {
      return;
    }

    axios
      .post("api/v1/commissions/markPaidFromUpload", payload, {
        headers: _getHeaders(),
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getAccountManagerSummaryRevenue: function (employeeID, params, callback) {
    if (
      !_checkAuth(
        () =>
          services.getAccountManagerSummaryRevenue(
            employeeID,
            params,
            callback
          ),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/employees/" + employeeID + "/summaryRevenue";

    axios
      .get(url, {
        headers: _getHeaders(),
        params: { ...params },
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getCurrentFiscalYear: function (callback) {
    if (!_checkAuth(() => services.getCurrentFiscalYear(callback), callback)) {
      return;
    }

    let url = "api/v1/fiscalYears/currentFiscalYear";

    axios
      .get(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getFiscalYears: function (callback) {
    if (!_checkAuth(() => services.getFiscalYears(callback), callback)) {
      return;
    }

    let url = "/api/v1/fiscalYears";

    axios
      .get(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getAccounts: function (businessUnit, callback) {
    if (!_checkAuth(() => services.getAccounts(businessUnit, callback), callback)) {
      return;
    }

    let url = "/api/v1/payments/accounts";
    const params = {};
    if (businessUnit) {
      params.businessUnit = businessUnit;
    }

    axios
      .get(url, {
        headers: _getHeaders(),
        params: params,
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getXeroRegions: function (businessUnit, callback) {
    if (!_checkAuth(() => services.getXeroRegions(businessUnit, callback), callback)) {
      return;
    }

    let url = "/api/v1/payments/regions";
    const params = {};
    if (businessUnit) {
      params.businessUnit = businessUnit;
    }

    axios
      .get(url, {
        headers: _getHeaders(),
        params: params,
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getXeroDepartments: function (businessUnit, callback) {
    if (!_checkAuth(() => services.getXeroDepartments(businessUnit, callback), callback)) {
      return;
    }

    let url = "/api/v1/payments/departments";
    const params = {};
    if (businessUnit) {
      params.businessUnit = businessUnit;
    }

    axios
      .get(url, {
        headers: _getHeaders(),
        params: params,
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  syncAllFromXero: function (businessUnit, callback) {
    if (!_checkAuth(() => services.syncAllFromXero(businessUnit, callback), callback)) {
      return;
    }

    let url = "/api/v1/payments/sync-all";
    const params = {
      businessUnit: businessUnit
    };

    axios
      .post(url, null, {
        headers: _getHeaders(),
        params: params,
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  createCustomExpense: function (data, callback) {
    if (!_checkAuth(() => services.createCustomExpense(data, callback), callback)) {
      return;
    }
    const url = "/api/v1/payments/custom-expenses";
    axios
      .post(url, data, { headers: _getHeaders() })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getCustomExpenses: function (params, callback) {
    if (!_checkAuth(() => services.getCustomExpenses(params, callback), callback)) {
      return;
    }
    axios
      .get("/api/v1/payments/custom-expenses/filter", { headers: _getHeaders(), params: params || {} })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getCustomExpense: function (id, callback) {
    if (!_checkAuth(() => services.getCustomExpense(id, callback), callback)) {
      return;
    }
    axios
      .get("/api/v1/payments/custom-expenses/" + id, { headers: _getHeaders() })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  updateCustomExpense: function (id, data, callback) {
    if (!_checkAuth(() => services.updateCustomExpense(id, data, callback), callback)) {
      return;
    }
    axios
      .put("/api/v1/payments/custom-expenses/" + id, data, { headers: _getHeaders() })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  deleteCustomExpense: function (id, callback) {
    if (!_checkAuth(() => services.deleteCustomExpense(id, callback), callback)) {
      return;
    }
    axios
      .delete("/api/v1/payments/custom-expenses/" + id, { headers: _getHeaders() })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  listCustomExpenseReceipts: function (invoiceId, callback) {
    if (!_checkAuth(() => services.listCustomExpenseReceipts(invoiceId, callback), callback)) {
      return;
    }
    const url = `/api/v1/payments/custom-expenses/${invoiceId}/receipt`;
    axios
      .get(url, { headers: _getHeaders() })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getReceiptParseAvailability: function (callback) {
    if (!_checkAuth(() => services.getReceiptParseAvailability(callback), callback)) {
      return;
    }
    axios
      .get("/api/v1/payments/receipts/parse/availability", { headers: _getHeaders() })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response && error.response.data ? error.response.data : null);
      });
  },
  parseReceipt: function (file, provider, callback, preparation) {
    if (typeof provider === "function") {
      callback = provider;
      provider = null;
      preparation = null;
    }
    if (!_checkAuth(() => services.parseReceipt(file, provider, callback, preparation), callback)) {
      return;
    }
    const url = "/api/v1/payments/receipts/parse";
    const formData = new FormData();
    formData.append("file", file);
    if (provider) {
      formData.append("provider", provider);
    }
    if (preparation) {
      formData.append("preparation", preparation);
    }
    axios
      .post(url, formData, {
        headers: {
          ..._getHeaders(),
          "Content-Type": "multipart/form-data"
        }
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response && error.response.data ? error.response.data : null);
      });
  },
  uploadCustomExpenseReceipt: function (invoiceId, file, callback, onProgress) {
    if (!_checkAuth(() => services.uploadCustomExpenseReceipt(invoiceId, file, callback, onProgress), callback)) {
      return;
    }
    const metadata = {
      originalFilename: file.name,
      contentType: resolveUploadContentType(file),
      sizeBytes: file.size,
    };
    axios
      .post("/api/v1/payments/custom-expenses/" + invoiceId + "/receipt/init", metadata, { headers: _getHeaders() })
      .then(function (initResponse) {
        const init = initResponse.data;
        const formData = new FormData();
        const fields = init.presignedFields || {};
        Object.keys(fields).forEach(function (key) {
          formData.append(key, fields[key]);
        });
        formData.append("file", file);
        return fetch(init.presignedUrl, { method: "POST", body: formData }).then(function (uploadResponse) {
          if (!uploadResponse.ok) {
            throw new Error("Staging upload failed");
          }
          return init;
        });
      })
      .then(function (init) {
        services.documentHubPollFinalizeUntilDone(
          init.documentUuid,
          init.versionUuid,
          onProgress || null,
          function (ok, result) {
            if (!ok) {
              callback(false, result);
              return;
            }
            if (result.phase === "duplicate_detected") {
              callback(false, {
                duplicate: true,
                documentUuid: init.documentUuid,
                versionUuid: init.versionUuid,
                duplicateInfo: result.data,
              });
              return;
            }
            callback(true, { ...(result.data || {}), documentUuid: init.documentUuid });
          }
        );
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response || error);
      });
  },

  finalizeCustomExpenseReceiptDuplicate: function (documentUuid, versionUuid, duplicateAction, linkToDocumentUuid, callback, onProgress) {
    const payload = { duplicateAction: duplicateAction };
    if (linkToDocumentUuid) {
      payload.linkToDocumentUuid = linkToDocumentUuid;
    }
    services.documentHubPollFinalizeUntilDone(documentUuid, versionUuid, onProgress, callback, payload);
  },

  deleteCustomExpenseReceipt: function (invoiceId, attachmentId, callback) {
    if (!_checkAuth(() => services.deleteCustomExpenseReceipt(invoiceId, attachmentId, callback), callback)) {
      return;
    }
    axios
      .delete("/api/v1/payments/custom-expenses/" + invoiceId + "/receipt/" + attachmentId, { headers: _getHeaders() })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  downloadCustomExpenseReceipt: function (invoiceId, attachmentId, filename, callback) {
    if (!_checkAuth(() => services.downloadCustomExpenseReceipt(invoiceId, attachmentId, filename, callback), callback)) {
      return;
    }
    const url = `/api/v1/payments/custom-expenses/${invoiceId}/receipt/${attachmentId}`;
    axios
      .get(url, {
        headers: _getHeaders(),
        maxRedirects: 0,
        validateStatus: function (status) {
          return (status >= 200 && status < 300) || status === 302;
        },
      })
      .then(function (response) {
        if (response.status === 302) {
          const redirectUrl = response.headers.location;
          const link = document.createElement("a");
          link.href = redirectUrl;
          link.download = filename || "receipt";
          link.rel = "noopener";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          if (callback) callback(true);
          return;
        }
        const blob = response.data;
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = filename || "receipt";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href);
        if (callback) callback(true);
      })
      .catch(function (error) {
        console.error(error);
        if (callback) callback(false, error.response);
      });
  },
  getLineItems: function (params, callback) {
    if (!_checkAuth(() => services.getLineItems(params, callback), callback)) {
      return;
    }
    let url = "/api/v1/xeroinvoices/line-items/filter";
    axios
      .get(url, {
        headers: _getHeaders(),
        params: params || {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getLineItemsAnalytics: function (params, callback) {
    if (!_checkAuth(() => services.getLineItemsAnalytics(params, callback), callback)) {
      return;
    }
    let url = "/api/v1/xeroinvoices/line-items/analytics";
    axios
      .get(url, {
        headers: _getHeaders(),
        params: params || {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  updateLineItemRegion: function (lineItemId, body, callback) {
    if (!_checkAuth(() => services.updateLineItemRegion(lineItemId, body, callback), callback)) {
      return;
    }
    const url = _lineItemOverrideUrl(body, lineItemId, "region");
    const payload = {
      xeroRegionId: body.xeroRegionId,
      businessUnit: body.businessUnit,
    };
    axios
      .patch(url, payload, { headers: _getHeaders() })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  updateLineItemDepartment: function (lineItemId, body, callback) {
    if (!_checkAuth(() => services.updateLineItemDepartment(lineItemId, body, callback), callback)) {
      return;
    }
    const url = _lineItemOverrideUrl(body, lineItemId, "department");
    const payload = {
      xeroDepartmentId: body.xeroDepartmentId,
      businessUnit: body.businessUnit,
    };
    axios
      .patch(url, payload, { headers: _getHeaders() })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getExpensesTypes: function (callback) {
    if (!_checkAuth(() => services.getExpensesTypes(callback), callback)) {
      return;
    }

    let url = "/api/v1/expense/types";
    axios
      .get(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  patchTechnologyOpportunityHeatmap: function (
    technologyOpportunityHeatmapID,
    data,
    callback
  ) {
    if (
      !_checkAuth(
        () =>
          services.patchTechnologyOpportunityHeatmap(
            technologyOpportunityHeatmapID,
            data,
            callback
          ),
        callback
      )
    ) {
      return;
    }

    let url =
      "/api/v1/technologyOpportunityHeatmap/" +
      technologyOpportunityHeatmapID.toString();

    axios
      .patch(url, data, {
        headers: _getHeaders(),
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },
  getTechnologyOpportunityHeatmap: function (
    technologyOpportunityHeatmapID,
    callback
  ) {
    if (
      !_checkAuth(
        () =>
          services.getTechnologyOpportunityHeatmap(
            technologyOpportunityHeatmapID,
            callback
          ),
        callback
      )
    ) {
      return;
    }

    let url =
      "/api/v1/technologyOpportunityHeatmap/" +
      technologyOpportunityHeatmapID.toString();

    axios
      .get(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getOpportunitiesPerCustomer: function (employeeID, params, callback) {
    if (
      !_checkAuth(
        () =>
          services.getOpportunitiesPerCustomer(employeeID, params, callback),
        callback
      )
    ) {
      return;
    }

    let url = "/api/v1/employees/" + employeeID + "/opportunitiesPerCustomer";

    axios
      .get(url, {
        headers: _getHeaders(),
        params: { ...params },
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getProjectsCurrentUserOpenQuotes: function (params, callback) {
    if (
      !_checkAuth(
        () => services.getProjectsCurrentUserOpenQuotes(params, callback),
        callback
      )
    ) {
      return;
    }

    let url = "/api/v1/projects/currentUser/openQuotes";

    axios
      .get(url, {
        headers: _getHeaders(),
        params: { ...params },
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getEmployeeProjects: function (
    employeeID,
    projectsType,
    params,
    additionalHeaders,
    callback
  ) {
    if (
      !_checkAuth(
        () =>
          services.getEmployeeProjects(
            employeeID,
            projectsType,
            params,
            additionalHeaders,
            callback
          ),
        callback
      )
    ) {
      return;
    }

    let url = "/api/v1/employees/" + employeeID + "/projects/" + projectsType;

    axios
      .get(url, {
        headers: { ..._getHeaders(), ...additionalHeaders },
        params: { ...params },
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  deleteNonQuotedOpportunity: function (nonQuotedOpportunityID, callback) {
    if (
      !_checkAuth(
        () =>
          services.deleteNonQuotedOpportunity(nonQuotedOpportunityID, callback),
        callback
      )
    ) {
      return;
    }

    let url =
      "/api/v1/nonQuotedOpportunities/" + nonQuotedOpportunityID.toString();

    axios
      .delete(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  // deleteNonQuotedOpportunityPromise: function(
  //   nonQuotedOpportunityID,
  //   callback
  // ) {
  //   if (
  //     !_checkAuth(
  //       () =>
  //         services.deleteNonQuotedOpportunityPromise(
  //           nonQuotedOpportunityID,
  //           callback
  //         ),
  //       callback
  //     )
  //   ) {
  //     return;
  //   }

  //   let url =
  //     "/api/v1/nonQuotedOpportunities/" +
  //     nonQuotedOpportunityID.toString();

  //   return axios
  //     .delete(
  //       url,
  //       {
  //         headers: _getHeaders(),
  //         params: {}
  //       }
  //     )
  // },
  patchNonQuotedOpportunity: function (nonQuotedOpportunityID, data, callback) {
    if (
      !_checkAuth(
        () =>
          services.patchNonQuotedOpportunity(
            nonQuotedOpportunityID,
            data,
            callback
          ),
        callback
      )
    ) {
      return;
    }

    let url =
      "/api/v1/nonQuotedOpportunities/" + nonQuotedOpportunityID.toString();

    axios
      .patch(url, data, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getNonQuotedOpportunity: function (nonQuotedOpportunityID, callback) {
    if (
      !_checkAuth(
        () =>
          services.getNonQuotedOpportunity(nonQuotedOpportunityID, callback),
        callback
      )
    ) {
      return;
    }

    let url =
      "/api/v1/nonQuotedOpportunities/" + nonQuotedOpportunityID.toString();

    axios
      .get(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  updateNonQuotedOpportunities: function (
    nonQuotedOpportunityID,
    data,
    callback
  ) {
    if (
      !_checkAuth(
        () =>
          services.updateNonQuotedOpportunities(
            nonQuotedOpportunityID,
            data,
            callback
          ),
        callback
      )
    ) {
      return;
    }

    let url =
      "/api/v1/nonQuotedOpportunities/" + nonQuotedOpportunityID.toString();

    axios
      .put(url, data, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  createNonQuotedOpportunities: function (data, callback) {
    if (
      !_checkAuth(
        () => services.createNonQuotedOpportunities(data, callback),
        callback
      )
    ) {
      return;
    }

    let url = "/api/v1/nonQuotedOpportunities";

    axios
      .post(url, data, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getAllNonQuotedOpportunities: function (callback) {
    if (
      !_checkAuth(() => services.getNonQuotedOpportunities(callback), callback)
    ) {
      return;
    }

    let url = "/api/v1/nonQuotedOpportunities";

    axios
      .get(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getNonQuotedOpportunities: function (employeeID, callback) {
    if (
      !_checkAuth(
        () => services.getNonQuotedOpportunities(employeeID, callback),
        callback
      )
    ) {
      return;
    }

    let url = "/api/v1/employees/" + employeeID + "/nonQuotedOpportunities";

    axios
      .get(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getRegions: function (callback) {
    if (!_checkAuth(() => services.getRegions(callback), callback)) {
      return;
    }

    let url = "/api/v1/regions";

    axios
      .get(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  deleteClient: function (clientID, callback) {
    if (
      !_checkAuth(() => services.deleteClient(clientID, callback), callback)
    ) {
      return;
    }

    let url = "/api/v1/clients/" + clientID.toString();

    axios
      .delete(url, {
        headers: _getHeaders(),
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },
  previewMergeClient: function (sourceClientID, targetClientId, callback) {
    if (
      !_checkAuth(
        () => services.previewMergeClient(sourceClientID, targetClientId, callback),
        callback
      )
    ) {
      return;
    }

    let url =
      "/api/v1/clients/" +
      sourceClientID.toString() +
      "/merge/preview?targetClientId=" +
      targetClientId.toString();

    axios
      .get(url, {
        headers: _getHeaders(),
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },
  mergeClient: function (sourceClientID, targetClientId, callback) {
    if (
      !_checkAuth(
        () => services.mergeClient(sourceClientID, targetClientId, callback),
        callback
      )
    ) {
      return;
    }

    let url = "/api/v1/clients/" + sourceClientID.toString() + "/merge";

    axios
      .post(
        url,
        { targetClientId: targetClientId },
        {
          headers: _getHeaders(),
        }
      )
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },
  patchClient: function (clientID, data, callback) {
    if (
      !_checkAuth(
        () => services.patchClient(clientID, data, callback),
        callback
      )
    ) {
      return;
    }

    let url = "/api/v1/clients/" + clientID.toString();

    axios
      .patch(url, data, {
        headers: _getHeaders(),
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },

  updateClient: function (clientID, data, callback) {
    if (
      !_checkAuth(
        () => services.updateClient(clientID, data, callback),
        callback
      )
    ) {
      return;
    }

    let url = "/api/v1/clients/" + clientID.toString();

    axios
      .put(url, data, {
        headers: _getHeaders(),
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },
  createClient: function (data, callback) {
    if (!_checkAuth(() => services.createClient(data, callback), callback)) {
      return;
    }

    let url = "/api/v1/clients";

    axios
      .post(url, data, {
        headers: _getHeaders(),
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },
  getClientContacts: function (clientID, callback) {
    if (
      !_checkAuth(
        () => services.getClientContacts(clientID, callback),
        callback
      )
    ) {
      return;
    }

    let url = "/api/v1/clients/" + clientID.toString() + "/contacts";

    axios
      .get(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getClient: function (clientID, callback) {
    if (!_checkAuth(() => services.getClient(clientID, callback), callback)) {
      return;
    }

    let url = "/api/v1/clients/" + clientID.toString();

    axios
      .get(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  // getClientsPromise: function(callback) {
  //   if (!_checkAuth(() => services.getClientsPromise(callback), callback)) {
  //     return;
  //   }

  //   let url = "/api/v1/clients";

  //   return axios
  //     .get(url, {
  //       headers: _getHeaders(),
  //       params: {}
  //     })
  // },
  getClients: function (params, callback) {
    if (!_checkAuth(() => services.getClients(params, callback), callback)) {
      return;
    }

    let url = "/api/v1/clients";

    axios
      .get(url, {
        headers: _getHeaders(),
        params: params ? params : {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },

  getClientsByName: function (params, callback) {
    if (
      !_checkAuth(() => services.getClientsByName(params, callback), callback)
    ) {
      return;
    }

    let url = "/api/v1/clients/search";
    let callID = "get:" + url;

    if (calls[callID]) {
      calls[callID].cancel("Request cancelled");
    }
    calls[callID] = axios.CancelToken.source();

    let config = {
      method: "get",
      url: url,
      headers: { ..._getHeaders() },
      params: {
        ...params,
      },
    };
    config.cancelToken = calls[callID].token;
    axios(config)
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        if (!axios.isCancel(error)) {
          console.error(error);
          callback(false, error.response);
        }
      });
  },

  // -------------------------
  // Partners (CRM)
  // -------------------------
  getPartner: function (partnerID, callback) {
    if (!_checkAuth(() => services.getPartner(partnerID, callback), callback)) {
      return;
    }

    let url = "/api/v1/partners/" + partnerID.toString();

    axios
      .get(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },

  getLeads: function (callback) {
    if (!_checkAuth(() => services.getLeads(callback), callback)) return;
    axios.get("/api/v1/leads", { headers: _getHeaders() })
      .then(r => callback(true, r.data)).catch(e => callback(false, e.response));
  },
  getLeadMessages: function (id, callback) {
    if (!_checkAuth(() => services.getLeadMessages(id, callback), callback)) return;
    axios.get(`/api/v1/leads/${id}/messages`, { headers: _getHeaders() })
      .then(r => callback(true, r.data)).catch(e => callback(false, e.response));
  },
  saveLead: function (id, data, callback) {
    if (!_checkAuth(() => services.saveLead(id, data, callback), callback)) return;
    axios({ method: id ? "put" : "post", url: id ? `/api/v1/leads/${id}` : "/api/v1/leads",
      headers: _getHeaders(), data })
      .then(r => callback(true, r.data)).catch(e => callback(false, e.response));
  },

  getPartners: function (params, callback) {
    if (!_checkAuth(() => services.getPartners(params, callback), callback)) {
      return;
    }

    let url = "/api/v1/partners";

    axios
      .get(url, {
        headers: _getHeaders(),
        params: params ? params : {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },

  createPartner: function (data, callback) {
    if (!_checkAuth(() => services.createPartner(data, callback), callback)) {
      return;
    }

    let url = "/api/v1/partners";

    axios
      .post(url, data, {
        headers: _getHeaders(),
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },

  updatePartner: function (partnerID, data, callback) {
    if (!_checkAuth(() => services.updatePartner(partnerID, data, callback), callback)) {
      return;
    }

    let url = "/api/v1/partners/" + partnerID.toString();

    axios
      .put(url, data, {
        headers: _getHeaders(),
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },

  deletePartner: function (partnerID, callback) {
    if (!_checkAuth(() => services.deletePartner(partnerID, callback), callback)) {
      return;
    }

    let url = "/api/v1/partners/" + partnerID.toString();

    axios
      .delete(url, {
        headers: _getHeaders(),
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },

  syncPartners: function (params, callback) {
    if (!_checkAuth(() => services.syncPartners(params, callback), callback)) {
      return;
    }

    let url = "/api/v1/partners/sync";

    axios
      .post(url, {}, {
        headers: _getHeaders(),
        params: params ? params : {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },

  // getProjectStatusPromise: function(callback) {
  //   if (
  //     !_checkAuth(() => services.getProjectStatusPromise(callback), callback)
  //   ) {
  //     return Promise.resolve({});
  //   }

  //   let url = "/api/v1/projectStatus";

  //   return axios.get(url, {
  //     headers: _getHeaders(),
  //     params: {}
  //   });
  // },
  getProjectStatus: function (callback) {
    if (!_checkAuth(() => services.getProjectStatus(callback), callback)) {
      return;
    }

    let url = "/api/v1/projectStatus";

    axios
      .get(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getProjectSummaries: function (callback) {
    if (!_checkAuth(() => services.getProjectSummaries(callback), callback)) {
      return;
    }

    let url = "/api/v1/projectSummaries";

    axios
      .get(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  // getProjectVersionHistoryByProjectID: function (projectID, callback) {
  //   if (
  //     !_checkAuth(
  //       () =>
  //         services.getProjectVersionHistoryByProjectID(
  //           projectID,
  //           callback
  //         ),
  //       callback
  //     )
  //   ) {
  //     return;
  //   }

  //   let url = "/api/v1/versionHistory/" + projectID;

  //   axios
  //     .get(url, {
  //       headers: _getHeaders(),
  //       params: {
  //       }
  //     })
  //     .then(function (response) {
  //       callback(true, response.data);
  //     })
  //     .catch(function (error) {
  //       console.error(error);
  //       callback(false, error.response);
  //     });
  // },
  getProjectVersionHistory: function (params, callback) {
    if (
      !_checkAuth(
        () => services.getProjectVersionHistory(params, callback),
        callback
      )
    ) {
      return;
    }

    let url = "/api/v1/versionHistory";

    axios
      .get(url, {
        headers: _getHeaders(),
        params,
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getProjectVersionHistoryDeltaChange: function (params, callback) {
    if (
      !_checkAuth(
        () => services.getProjectVersionHistoryDeltaChange(params, callback),
        callback
      )
    ) {
      return;
    }

    let url = "/api/v1/projectVersionHistoryDeltaChange";

    axios
      .get(url, {
        headers: _getHeaders(),
        params,
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getProjectVersionHistoryDailyStats: function (params, callback) {
    if (
      !_checkAuth(
        () => services.getProjectVersionHistoryDailyStats(params, callback),
        callback
      )
    ) {
      return;
    }

    let url = "/api/v1/projectVersionHistoryDailyStats";

    axios
      .get(url, {
        headers: _getHeaders(),
        params,
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getClientVersionHistory: function (params, callback) {
    if (
      !_checkAuth(
        () => services.getClientVersionHistory(params, callback),
        callback
      )
    ) {
      return;
    }

    let url = "/api/v1/clientVersionHistory";

    axios
      .get(url, {
        headers: _getHeaders(),
        params,
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  updateCommissionStructureById: function (
    data,
    commissionStructureId,
    callback
  ) {
    if (
      !_checkAuth(
        () =>
          services.updateCommissionStructureById(
            data,
            commissionStructureId,
            callback
          ),
        callback
      )
    ) {
      return;
    }

    let url =
      "api/v1/commissionStructuresManagement/commissionStructures/" +
      commissionStructureId.toString();

    axios
      .put(url, data, {
        headers: _getHeaders(),
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },
  getCommissionStructureById: function (commissionStructureId, callback) {
    if (
      !_checkAuth(
        () =>
          services.getCommissionStructureById(commissionStructureId, callback),
        callback
      )
    ) {
      return;
    }

    let url =
      "/api/v1/commissionStructuresManagement/commissionStructures/" +
      commissionStructureId.toString();

    axios
      .get(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getCommissionStructure: function (callback) {
    if (
      !_checkAuth(() => services.getCommissionStructure(callback), callback)
    ) {
      return;
    }

    let url = "/api/v1/commissionStructuresManagement/commissionStructures";

    axios
      .get(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  deleteEmployee: function (employeeId, callback) {
    if (
      !_checkAuth(() => services.deleteEmployee(employeeId, callback), callback)
    ) {
      return;
    }

    let url = "api/v1/admin/employees/" + employeeId.toString();

    axios
      .delete(url, {
        headers: _getHeaders(),
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },

  patchEmployee: function (employeeData, employeeId, callback) {
    if (
      !_checkAuth(
        () => services.patchEmployee(employeeData, employeeId, callback),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/admin/employees/" + employeeId.toString();

    axios
      .patch(url, employeeData, {
        headers: _getHeaders(),
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },
  updateEmployee: function (employeeData, employeeId, callback) {
    if (
      !_checkAuth(
        () => services.updateEmployee(employeeData, employeeId, callback),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/admin/employees/" + employeeId.toString();

    axios
      .put(url, employeeData, {
        headers: _getHeaders(),
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },
  getEmployee: function (employeeId, callback) {
    if (
      !_checkAuth(() => services.getEmployee(employeeId, callback), callback)
    ) {
      return;
    }

    let url = "/api/v1/admin/employees/" + employeeId.toString();

    axios
      .get(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  createEmployee: function (employeeData, callback) {
    if (
      !_checkAuth(
        () => services.createEmployee(employeeData, callback),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/admin/employees";

    axios
      .post(url, employeeData, {
        headers: _getHeaders(),
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },
  bulkUpsertEmployees: function (payload, callback) {
    if (
      !_checkAuth(
        () => services.bulkUpsertEmployees(payload, callback),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/admin/employees/bulk-upsert";

    axios
      .post(url, payload, {
        headers: _getHeaders(),
      })
      .then(function (response) {
        let data = response.data;
        if (typeof data === "string") {
          try {
            data = JSON.parse(data);
          } catch (e) {
            console.error(e);
          }
        }
        callback(true, data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },
  getRoles: function (callback) {
    if (!_checkAuth(() => services.getRoles(callback), callback)) {
      return;
    }

    let url = "/api/v1/roles";

    axios
      .get(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getCreateEditEmployeeData: function (callback) {
    if (
      !_checkAuth(() => services.getCreateEditEmployeeData(callback), callback)
    ) {
      return;
    }

    axios
      .all([
        axios.get("/api/v1/roles", {
          headers: _getHeaders(),
          params: {},
        }),

        axios.get("api/v1/regions", {
          headers: _getHeaders(),
          params: {},
        }),

        axios.get("/api/v1/business-entities", {
          headers: _getHeaders(),
          params: {},
        }),
      ])
      .then(function (response) {
        callback(true, response);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getEmployees: function (params, callback) {
    if (!_checkAuth(() => services.getEmployees(params, callback), callback)) {
      return;
    }

    let url = "/api/v1/employees/directory";

    axios
      .get(url, {
        headers: _getHeaders(),
        params: params,
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getAdminEmployees: function (params, callback) {
    if (!_checkAuth(() => services.getAdminEmployees(params, callback), callback)) {
      return;
    }

    let url = "/api/v1/admin/employees";

    axios
      .get(url, {
        headers: _getHeaders(),
        params: params,
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  requestPayrollRoleChallenge: function (targetEmployeeId, action, callback) {
    if (
      !_checkAuth(
        () => services.requestPayrollRoleChallenge(targetEmployeeId, action, callback),
        callback
      )
    ) {
      return;
    }
    axios
      .post(
        "/api/v1/payroll/role-assignments/challenge",
        { targetEmployeeId, action },
        { headers: _getHeaders() }
      )
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },
  confirmPayrollRoleChange: function (challengeId, code, callback) {
    if (
      !_checkAuth(
        () => services.confirmPayrollRoleChange(challengeId, code, callback),
        callback
      )
    ) {
      return;
    }
    axios
      .post(
        "/api/v1/payroll/role-assignments/confirm",
        { challengeId, code },
        { headers: _getHeaders() }
      )
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },
  getProjectsTotalsCurrentUser: function (params, headers, callback) {
    if (
      !_checkAuth(
        () => services.getProjectsTotalsCurrentUser(params, headers, callback),
        callback
      )
    ) {
      return;
    }

    let url = "/api/v1/projects/currentUser/totals";

    axios
      .get(url, {
        headers: { ..._getHeaders(), ...headers },
        params: {
          ...params,
        },
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getProjectsTotals: function (params, headers, callback) {
    if (
      !_checkAuth(
        () => services.getProjectsTotals(params, headers, callback),
        callback
      )
    ) {
      return;
    }

    let url = "/api/v1/projects/totals";

    axios
      .get(url, {
        headers: { ..._getHeaders(), ...headers },
        params: {
          ...params,
        },
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getProjectsPageQueryAccMngrPracticeLead: function (
    page,
    pageSize,
    params,
    headers,
    responseType,
    callback
  ) {
    if (
      !_checkAuth(
        () =>
          services.getProjectsPageQueryAccMngrPracticeLead(
            page,
            pageSize,
            params,
            headers,
            responseType,
            callback
          ),
        callback
      )
    ) {
      return;
    }

    let url = "/api/v1/projects/currentUser";

    axios
      .get(url, {
        headers: { ..._getHeaders(), ...headers },
        responseType: responseType,
        params: {
          page: page,
          size: pageSize,
          ...params,
        },
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getProjectsNumber: function (params, callback) {
    if (
      !_checkAuth(() => services.getProjectsNumber(params, callback), callback)
    ) {
      return;
    }

    let url = "/api/v1/projects/projectsNumber";
    let callID = "get:" + url;

    if (calls[callID]) {
      calls[callID].cancel("Request cancelled");
    }
    calls[callID] = axios.CancelToken.source();

    let config = {
      method: "get",
      url: url,
      headers: { ..._getHeaders() },
      params: {
        ...params,
      },
    };
    config.cancelToken = calls[callID].token;
    axios(config)
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getQuotesProjectNumbers: function (params, callback) {
    if (
      !_checkAuth(
        () => services.getQuotesProjectNumbers(params, callback),
        callback
      )
    ) {
      return;
    }

    let url = "/api/v1/quotes/projectsNumber";
    let callID = "get:" + url;

    if (calls[callID]) {
      calls[callID].cancel("Request cancelled");
    }
    calls[callID] = axios.CancelToken.source();

    let config = {
      method: "get",
      url: url,
      headers: { ..._getHeaders() },
      params: {
        ...params,
      },
    };
    config.cancelToken = calls[callID].token;
    axios(config)
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getProjectsAnnuityRenewals: function (params, callback) {
    if (
      !_checkAuth(
        () => services.getProjectsAnnuityRenewals(params, callback),
        callback
      )
    ) {
      return;
    }

    let url = "/api/v1/projects/annuityRenewals";
    axios
      .get(url, {
        headers: _getHeaders(),
        params,
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getProjectsPageQuery: function (
    page,
    pageSize,
    params,
    headers,
    responseType,
    callback
  ) {
    if (
      !_checkAuth(
        () =>
          services.getProjectsPageQuery(
            page,
            pageSize,
            params,
            headers,
            responseType,
            callback
          ),
        callback
      )
    ) {
      return;
    }

    let url = "/api/v1/projects";
    let callID = "get:" + url;

    if (calls[callID]) {
      calls[callID].cancel("Request cancelled");
    }
    calls[callID] = axios.CancelToken.source();

    let config = {
      method: "get",
      url: url,
      headers: { ..._getHeaders(), ...headers },
      responseType: responseType,
      params: {
        page: page,
        size: pageSize,
        ...params,
      },
    };
    config.cancelToken = calls[callID].token;
    axios(config)
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });

    // axios
    //   .get(url, {
    //     headers: { ..._getHeaders(), ...headers },
    //     responseType: responseType,
    //     params: {
    //       page: page,
    //       size: pageSize,
    //       ...params
    //     }
    //   })
    //   .then(function (response) {
    //     callback(true, response.data);
    //   })
    //   .catch(function (error) {
    //     console.error(error);
    //     callback(false, error.response);
    //   });
  },
  getProjectsNegativeGroupedGrossProfit: function (params, callback) {
    if (
      !_checkAuth(
        () => services.getProjectsNegativeGroupedGrossProfit(params, callback),
        callback
      )
    ) {
      return;
    }

    let url = "/api/v1/projects/negativeGroupedGrossProfit";
    axios
      .get(url, {
        headers: _getHeaders(),
        params,
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },
  updateProjectForecasting: function (
    projectForecastingId,
    projectForecastingObj,
    callback
  ) {
    if (
      !_checkAuth(
        () =>
          services.updateProjectForecasting(
            projectForecastingId,
            projectForecastingObj,
            callback
          ),
        callback
      )
    ) {
      return;
    }

    let url =
      "api/v1/projects/projectForecasting/" + projectForecastingId.toString();

    axios
      .put(url, projectForecastingObj, {
        headers: _getHeaders(),
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },
  getProjectForecasting: function (projectId, callback) {
    if (
      !_checkAuth(
        () => services.getProjectForecasting(projectId, callback),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/projects/projectForecasting/" + projectId.toString();

    axios
      .get(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },
  patchProject: function (projectId, projectData, callback) {
    if (
      !_checkAuth(
        () => services.patchProject(projectId, projectData, callback),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/projects/" + projectId.toString();

    axios
      .patch(url, projectData, {
        headers: _getHeaders(),
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },
  // updateProjectRecurrentWon: function (
  //   projectId,
  //   projectData,
  //   params,
  //   callback
  // ) {
  //   if (
  //     !_checkAuth(
  //       () =>
  //         services.updateProjectRecurrentWon(
  //           projectId,
  //           projectData,
  //           params,
  //           callback
  //         ),
  //       callback
  //     )
  //   ) {
  //     return;
  //   }

  //   let url = "api/v1/projects/" + projectId.toString() + "/wonStage";

  //   axios
  //     .put(url, projectData, {
  //       headers: _getHeaders(),
  //       params: { ...params }
  //     })
  //     .then(function (response) {
  //       callback(true, response.data);
  //     })
  //     .catch(function (error) {
  //       callback(false, error.response);
  //     });
  // },
  updateProject: function (projectId, projectData, params, callback) {
    if (
      !_checkAuth(
        () => services.updateProject(projectId, projectData, params, callback),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/projects/" + projectId.toString();

    axios
      .put(url, projectData, {
        headers: _getHeaders(),
        params: { ...params },
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },
  getProject: function (projectId, callback) {
    if (!_checkAuth(() => services.getProject(projectId, callback), callback)) {
      return;
    }

    let url = "api/v1/projects/" + projectId.toString();

    axios
      .get(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        try {
          callback(true, _coerceJsonResponse(response.data));
        } catch (processingError) {
          console.error("getProject response processing failed", processingError);
          callback(false, processingError);
        }
      })
      .catch(function (error) {
        console.error("getProject request failed", error);
        callback(false, error.response || error);
      });
  },
  deleteProject: function (projectID, callback) {
    if (
      !_checkAuth(() => services.deleteProject(projectID, callback), callback)
    ) {
      return;
    }

    let url = "api/v1/projects/" + projectID.toString();

    axios
      .delete(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },
  deleteQuoteByID: function (quoteID, callback) {
    if (
      !_checkAuth(() => services.deleteQuoteByID(quoteID, callback), callback)
    ) {
      return;
    }

    let url = "api/v1/quotes/" + quoteID.toString();

    axios
      .delete(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },
  getQuoteByID: function (quoteID, headers, responseType, callback) {
    if (
      !_checkAuth(
        () => services.getQuoteByID(quoteID, headers, responseType, callback),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/quotes/" + quoteID.toString();

    axios
      .get(url, {
        headers: { ..._getHeaders(), ...headers },
        responseType: responseType,
        params: {},
      })
      .then(function (response) {
        callback(true, response);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },
  getProjectByQuoteID: function (quoteID, callback) {
    if (
      !_checkAuth(
        () => services.getProjectByQuoteID(quoteID, callback),
        callback
      )
    ) {
      return;
    }

    let url = `api/v1/quotes/${quoteID.toString()}/project`;

    axios
      .get(url, {
        headers: { ..._getHeaders() },
        params: {},
      })
      .then(function (response) {
        callback(true, response);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },
  listQuotes: function (
    page,
    pageSize,
    params,
    headers,
    responseType,
    callback
  ) {
    if (
      !_checkAuth(
        () =>
          services.listQuotes(
            page,
            pageSize,
            params,
            headers,
            responseType,
            callback
          ),
        callback
      )
    ) {
      return;
    }

    let url = "/api/v1/quotes";

    axios
      .get(url, {
        headers: { ..._getHeaders(), ...headers },
        responseType: responseType,
        params: {
          page: page,
          size: pageSize,
          ...params,
        },
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  updateQuoteByID: function (quoteID, data, headers, callback) {
    if (
      !_checkAuth(
        () => services.updateQuoteByID(quoteID, data, headers, callback),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/quotes/" + quoteID;

    axios
      .put(url, data, {
        headers: { ..._getHeaders(), ...headers },
        responseType: "arraybuffer",
      })
      .then(function (response) {
        callback(true, response);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },
  createQuote: function (data, headers, callback) {
    if (
      !_checkAuth(() => services.createQuote(data, headers, callback), callback)
    ) {
      return;
    }

    let url = "api/v1/quotes";

    axios
      .post(url, data, {
        headers: { ..._getHeaders(), ...headers },
        responseType: "arraybuffer",
      })
      .then(function (response) {
        callback(true, response);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },
  getInvoiceNumbers: function (params, callback) {
    if (
      !_checkAuth(() => services.getInvoiceNumbers(params, callback), callback)
    ) {
      return;
    }

    let url = "/api/v1/invoices/invoicesNumbers";
    let callID = "get:" + url;

    if (calls[callID]) {
      calls[callID].cancel("Request cancelled");
    }
    calls[callID] = axios.CancelToken.source();

    let config = {
      method: "get",
      url: url,
      headers: { ..._getHeaders() },
      params: {
        ...params,
      },
    };
    config.cancelToken = calls[callID].token;
    axios(config)
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getInvoiceByID: function (quoteID, headers, responseType, callback) {
    if (
      !_checkAuth(
        () => services.getInvoiceByID(quoteID, headers, responseType, callback),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/invoices/" + quoteID.toString();

    axios
      .get(url, {
        headers: { ..._getHeaders(), ...headers },
        responseType: responseType,
        params: {},
      })
      .then(function (response) {
        callback(true, response);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },
  deleteInvoiceByID: function (invoiceID, callback) {
    if (
      !_checkAuth(
        () => services.deleteInvoiceByID(invoiceID, callback),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/invoices/" + invoiceID.toString();

    axios
      .delete(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },
  listInvoices: function (
    page,
    pageSize,
    params,
    headers,
    responseType,
    callback
  ) {
    if (
      !_checkAuth(
        () =>
          services.listInvoices(
            page,
            pageSize,
            params,
            headers,
            responseType,
            callback
          ),
        callback
      )
    ) {
      return;
    }

    let url = "/api/v1/invoices";

    axios
      .get(url, {
        headers: { ..._getHeaders(), ...headers },
        responseType: responseType,
        params: {
          page: page,
          size: pageSize,
          ...params,
        },
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  updateInvoiceByID: function (
    invoiceID,
    data,
    headers,
    responseType,
    callback
  ) {
    if (
      !_checkAuth(
        () =>
          services.updateInvoiceByID(
            invoiceID,
            data,
            headers,
            responseType,
            callback
          ),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/invoices/" + invoiceID;

    axios
      .put(url, data, {
        headers: { ..._getHeaders(), ...headers },
        responseType,
      })
      .then(function (response) {
        callback(true, response);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },
  createInvoice: function (data, headers, responseType, callback) {
    if (
      !_checkAuth(
        () => services.createInvoice(data, headers, responseType, callback),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/invoices";

    axios
      .post(url, data, {
        headers: { ..._getHeaders(), ...headers },
        responseType,
      })
      .then(function (response) {
        callback(true, response);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },

  // Purchase Orders
  getPurchaseOrderByID: function (ID, headers, responseType, callback) {
    if (
      !_checkAuth(
        () =>
          services.getPurchaseOrderByID(ID, headers, responseType, callback),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/purchaseorders/" + ID.toString();

    axios
      .get(url, {
        headers: { ..._getHeaders(), ...headers },
        responseType: responseType,
        params: {},
      })
      .then(function (response) {
        callback(true, response);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },
  deletePurchaseOrderByID: function (ID, callback) {
    if (
      !_checkAuth(
        () => services.deletePurchaseOrderByID(ID, callback),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/purchaseorders/" + ID.toString();

    axios
      .delete(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },
  listPurchaseOrders: function (
    page,
    pageSize,
    params,
    headers,
    responseType,
    callback
  ) {
    if (
      !_checkAuth(
        () =>
          services.listPurchaseOrders(
            page,
            pageSize,
            params,
            headers,
            responseType,
            callback
          ),
        callback
      )
    ) {
      return;
    }

    let url = "/api/v1/purchaseorders";

    axios
      .get(url, {
        headers: { ..._getHeaders(), ...headers },
        responseType: responseType,
        params: {
          page: page,
          size: pageSize,
          ...params,
        },
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  updatePurchaseOrderByID: function (
    ID,
    data,
    headers,
    responseType,
    callback
  ) {
    if (
      !_checkAuth(
        () =>
          services.updatePurchaseOrderByID(
            ID,
            data,
            headers,
            responseType,
            callback
          ),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/purchaseorders/" + ID;

    axios
      .put(url, data, {
        headers: { ..._getHeaders(), ...headers },
        responseType,
      })
      .then(function (response) {
        callback(true, response);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },
  createPurchaseOrder: function (data, headers, responseType, callback) {
    if (
      !_checkAuth(
        () =>
          services.createPurchaseOrder(data, headers, responseType, callback),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/purchaseorders";

    axios
      .post(url, data, {
        headers: { ..._getHeaders(), ...headers },
        responseType,
      })
      .then(function (response) {
        callback(true, response);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },

  verifyQuoteRefs: function (data, callback) {
    if (
      !_checkAuth(
        () =>
          services.verifyQuoteRefs(data, callback),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/purchaseorders/quoteRefs";

    axios
      .post(url, data, {
        headers: { ..._getHeaders()},
      })
      .then(function (response) {
        callback(true, response);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },
  // --------- end Purchase Orders -------------------

  listRenewals: function (
    page,
    pageSize,
    params,
    headers,
    responseType,
    callback
  ) {
    if (
      !_checkAuth(
        () =>
          services.listRenewals(
            page,
            pageSize,
            params,
            headers,
            responseType,
            callback
          ),
        callback
      )
    ) {
      return;
    }

    let url = "/api/v1/renewals";

    axios
      .get(url, {
        headers: { ..._getHeaders(), ...headers },
        responseType: responseType,
        params: {
          page: page,
          size: pageSize,
          ...params,
        },
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  createProject: function (projectData, callback) {
    if (
      !_checkAuth(() => services.createProject(projectData, callback), callback)
    ) {
      return;
    }

    let url = "api/v1/projects";

    axios
      .post(url, projectData, {
        headers: _getHeaders(),
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },

  getSystemLists: function (callback) {
    if (!_checkAuth(() => services.getSystemLists(callback), callback)) {
      return;
    }

    let url = "/api/v1/internalSystem/lists";

    axios
      .get(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },
  createExpenses: function (data,fromModal, callback) {
    if (
      !_checkAuth(
        () => services.createExpenses(data,fromModal,callback),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/expenses/"+fromModal;

    axios
      .post(url, data, {
        headers: _getHeaders(),
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getExpensesTotals: function (regionId,fyId,callback) {
    if (!_checkAuth(() => services.getExpensesTotals(regionId,fyId,callback), callback)) {
      return;
    }
    if(null===fyId){
      return;
    }
    let url = "/api/v1/expense/totals/"+ regionId+"/"+fyId;
    axios
      .get(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getFiscalYearExpensesTotals: function (fyId,callback) {
    if (!_checkAuth(() => services.getExpensesTotals(fyId,callback), callback)) {
      return;
    }
    if(null===fyId){
      return;
    }
    let url = "/api/v1/expenses/totals/"+fyId;
    axios
      .get(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getUserLog: function (params, callback) {

    if (!_checkAuth(() => services.getUserLog(params, callback), callback)) {
      return;
    }
    let url = "api/v1/userLog";
    axios
      .get(url, {
        headers: _getHeaders(), // Include authentication headers
        params: {
          page: params.page || 0,
          size: params.size || 10,
          sort: params.sort || "createdAt,desc",
        },
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getIndustryRevenue: function (callback) {
    if (!_checkAuth(() => services.getIndustryRevenue(callback), callback)) {
      return;
    }

    let url = "api/v1/industryRevenue";

    axios
      .get(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getProjectTypeRevenue: function (callback) {
    if (!_checkAuth(() => services.getProjectTypeRevenue(callback), callback)) {
      return;
    }

    let url = "api/v1/projectTypeRevenue";

    axios
      .get(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getProjectTechnologyTypeRevenue: function (callback) {
    if (!_checkAuth(() => services.getProjectTechnologyTypeRevenue(callback), callback)) {
      return;
    }

    let url = "api/v1/projectTechnologyTypeRevenue";

    axios
      .get(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getClientTypeRevenue: function (limit, callback) {
    if (!_checkAuth(() => services.getClientTypeRevenue(limit, callback), callback)) {
      return;
    }

    let url = "api/v1/clientTypeRevenue";

    axios
      .get(url, {
        headers: _getHeaders(),
        params: { limit: limit },
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  validateProjectNumber: function (params, callback) {
    if (!_checkAuth(() => services.validateProjectNumber(params, callback), callback)) {
      return;
    }
    axios
      .get("api/v1/projects/validateProjectNumber", {
        headers: _getHeaders(),
        params: params,
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
    getCashFlow: function (callback) {
    if (!_checkAuth(() => services.getCashFlow(callback), callback)) {
      return;
    }

    let url = "api/v1/cashFlow";

    axios
      .get(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  putCashFlow: function (body, callback) {
    if (
      !_checkAuth(
        () => services.putCashFlow(body,  callback),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/cashFlow";
    axios
      .put(url, body, {
        headers: _getHeaders(),
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
    getAnnualCashFlow: function (callback) {
    if (!_checkAuth(() => services.getAnnualCashFlow(callback), callback)) {
      return;
    }

    let url = "api/v1/annualCashFlow";

    axios
      .get(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  putAnnualCashFlow: function (body, callback) {
    if (
      !_checkAuth(
        () => services.putAnnualCashFlow(body,  callback),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/annualCashFlow";
    axios
      .put(url, body, {
        headers: _getHeaders(),
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },

  // Asset Parts API methods
  getAssetPartCategories: function (params, callback) {
    if (!_checkAuth(() => services.getAssetPartCategories(params, callback), callback)) {
      return;
    }

    let url = "api/v1/assetParts/categories";

    axios
      .get(url, {
        headers: _getHeaders(),
        params: params,
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },

  getAssetPartSubCategories: function (params, callback) {
    if (!_checkAuth(() => services.getAssetPartSubCategories(params, callback), callback)) {
      return;
    }

    let url = "api/v1/assetParts/subCategories";

    axios
      .get(url, {
        headers: _getHeaders(),
        params: params,
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },

  getAssetParts: function (params, callback) {
    if (!_checkAuth(() => services.getAssetParts(params, callback), callback)) {
      return;
    }

    let url = "api/v1/assetParts";

    axios
      .get(url, {
        headers: _getHeaders(),
        params: params,
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },

  getAssetPartById: function (id, callback) {
    if (!_checkAuth(() => services.getAssetPartById(id, callback), callback)) {
      return;
    }

    let url = "api/v1/assetParts/" + id;

    axios
      .get(url, {
        headers: _getHeaders(),
        params: {},
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },

  getAssetPartIds: function (data, callback) {
    if (!_checkAuth(() => services.getAssetPartIds(data, callback), callback)) {
      return;
    }

    let url = "api/v1/assetParts/partIds";

    axios
      .post(url, data, {
        headers: _getHeaders(),
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },

  createAssetParts: function (assetPartsData, callback) {
    if (!_checkAuth(() => services.createAssetParts(assetPartsData, callback), callback)) {
      return;
    }

    let url = "api/v1/assetParts";

    axios
      .post(url, assetPartsData, {
        headers: _getHeaders(),
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },

   updateExpensesDetails: function (expenseId, ExpenseDetailsData, callback) {
    if (!_checkAuth(() => services.updateExpensesDetails(expenseId, ExpenseDetailsData, callback), callback)) {
      return;
    }

    let url = "api/v1/expensesDetails/" + expenseId;

    axios
      .post(url, ExpenseDetailsData, {
        headers: _getHeaders(),
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
  getExpensesDetails: function ( ExpenseDetailsData, callback) {
    if (!_checkAuth(() => services.updateExpensesDetails(ExpenseDetailsData, callback), callback)) {
      return;
    }

    let url = "api/v1/expensesDetails";

    axios
      .post(url, ExpenseDetailsData, {
        headers: _getHeaders(),
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },
   deleteExpensesDetailsById: function (id, callback) {
    if (
      !_checkAuth(
        () => services.deleteExpensesDetailsById(id,callback),
        callback
      )
    ) {
      return;
    }

    let url = "api/v1/expensesDetails/"+id;
    axios
      .delete(url, {
        headers: _getHeaders(),
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },

  /** Document Hub API (Phase 2) */
  documentHubInitDocument: function (payload, callback) {
    if (!_checkAuth(() => services.documentHubInitDocument(payload, callback), callback)) {
      return;
    }
    axios
      .post("/api/v1/documenthub/documents", payload, { headers: _getHeaders() })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },

  documentHubInitNewVersion: function (documentUuid, payload, callback) {
    if (!_checkAuth(() => services.documentHubInitNewVersion(documentUuid, payload, callback), callback)) {
      return;
    }
    axios
      .post("/api/v1/documenthub/documents/" + documentUuid + "/versions", payload, { headers: _getHeaders() })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },

  documentHubFinalizeVersion: function (documentUuid, versionUuid, payload, callback) {
    if (!_checkAuth(() => services.documentHubFinalizeVersion(documentUuid, versionUuid, payload, callback), callback)) {
      return;
    }
    axios
      .post(
        "/api/v1/documenthub/documents/" + documentUuid + "/versions/" + versionUuid + "/finalize",
        payload || {},
        {
          headers: _getHeaders(),
          validateStatus: function (status) {
            return (status >= 200 && status < 300) || status === 202 || status === 409 || status === 503;
          },
        }
      )
      .then(function (response) {
        callback(true, { status: response.status, data: response.data });
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },

  documentHubGetVersionStatus: function (documentUuid, versionUuid, callback) {
    if (!_checkAuth(() => services.documentHubGetVersionStatus(documentUuid, versionUuid, callback), callback)) {
      return;
    }
    axios
      .get("/api/v1/documenthub/documents/" + documentUuid + "/versions/" + versionUuid + "/status", {
        headers: _getHeaders(),
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },

  documentHubTransitionLifecycle: function (documentUuid, toState, callback) {
    if (!_checkAuth(() => services.documentHubTransitionLifecycle(documentUuid, toState, callback), callback)) {
      return;
    }
    axios
      .post("/api/v1/documenthub/documents/" + documentUuid + "/lifecycle", { toState: toState }, {
        headers: _getHeaders(),
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },

  documentHubGetDocument: function (documentUuid, callback) {
    if (!_checkAuth(() => services.documentHubGetDocument(documentUuid, callback), callback)) {
      return;
    }
    axios
      .get("/api/v1/documenthub/documents/" + documentUuid, { headers: _getHeaders() })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },

  documentHubIndexDocumentContents: function (documentUuid, provider, callback) {
    if (typeof provider === "function") {
      callback = provider;
      provider = null;
    }
    if (!_checkAuth(() => services.documentHubIndexDocumentContents(documentUuid, provider, callback), callback)) {
      return;
    }
    const providerQuery = provider ? "?provider=" + encodeURIComponent(provider) : "";
    axios
      .post("/api/v1/documenthub/documents/" + documentUuid + "/content-index" + providerQuery, {}, { headers: _getHeaders() })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },

  documentHubSearchDocuments: function (params, callback) {
    if (!_checkAuth(() => services.documentHubSearchDocuments(params, callback), callback)) {
      return;
    }
    axios
      .get("/api/v1/documenthub/documents/search", { headers: _getHeaders(), params: params })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },

  documentHubListRequirementRules: function (callback) {
    if (!_checkAuth(() => services.documentHubListRequirementRules(callback), callback)) {
      return;
    }
    axios
      .get("/api/v1/documenthub/requirement-rules", { headers: _getHeaders() })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },

  documentHubEvaluateRequirementRules: function (params, callback) {
    if (!_checkAuth(() => services.documentHubEvaluateRequirementRules(params, callback), callback)) {
      return;
    }
    axios
      .get("/api/v1/documenthub/requirement-rules/evaluate", { headers: _getHeaders(), params: params })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },

  documentHubListClassPolicies: function (callback) {
    if (!_checkAuth(() => services.documentHubListClassPolicies(callback), callback)) {
      return;
    }
    axios
      .get("/api/v1/documenthub/class-policies", { headers: _getHeaders() })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },

  documentHubGetViewTree: function (params, callback) {
    if (!_checkAuth(() => services.documentHubGetViewTree(params, callback), callback)) {
      return;
    }
    axios
      .get("/api/v1/documenthub/views/tree", { headers: _getHeaders(), params: params })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },

  documentHubGetEntityNode: function (params, callback) {
    if (!_checkAuth(() => services.documentHubGetEntityNode(params, callback), callback)) {
      return;
    }
    axios
      .get("/api/v1/documenthub/views/entity-node", { headers: _getHeaders(), params: params })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },

  documentHubSearchWithinEntity: function (params, callback) {
    if (!_checkAuth(() => services.documentHubSearchWithinEntity(params, callback), callback)) {
      return;
    }
    axios
      .get("/api/v1/documenthub/views/entity-search", { headers: _getHeaders(), params: params })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },

  documentHubRestoreVersion: function (documentUuid, versionUuid, callback) {
    if (!_checkAuth(() => services.documentHubRestoreVersion(documentUuid, versionUuid, callback), callback)) {
      return;
    }
    axios
      .post("/api/v1/documenthub/documents/" + documentUuid + "/versions/" + versionUuid + "/restore", {}, {
        headers: _getHeaders(),
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },

  /** Document Hub share & ACL (Phase 3) */
  documentHubCreateShare: function (documentUuid, payload, callback) {
    if (!_checkAuth(() => services.documentHubCreateShare(documentUuid, payload, callback), callback)) {
      return;
    }
    axios
      .post("/api/v1/documenthub/documents/" + documentUuid + "/shares", payload, { headers: _getHeaders() })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },

  documentHubRevokeShare: function (shareId, callback) {
    if (!_checkAuth(() => services.documentHubRevokeShare(shareId, callback), callback)) {
      return;
    }
    axios
      .delete("/api/v1/documenthub/shares/" + shareId, { headers: _getHeaders() })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },

  documentHubResolveShare: function (token, password, callback) {
    const params = password ? { password: password } : {};
    axios
      .get("/api/v1/documenthub/shares/" + encodeURIComponent(token), { params: params })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },

  documentHubShareDownloadUrl: function (token, password) {
    let url = "/api/v1/documenthub/shares/" + encodeURIComponent(token) + "/download";
    if (password) {
      url += "?password=" + encodeURIComponent(password);
    }
    return url;
  },

  documentHubShareViewUrl: function (token, password) {
    let url = "/api/v1/documenthub/shares/" + encodeURIComponent(token) + "/view";
    if (password) {
      url += "?password=" + encodeURIComponent(password);
    }
    return url;
  },

  documentHubGrantAcl: function (documentUuid, payload, callback) {
    if (!_checkAuth(() => services.documentHubGrantAcl(documentUuid, payload, callback), callback)) {
      return;
    }
    axios
      .post("/api/v1/documenthub/documents/" + documentUuid + "/acl", payload, { headers: _getHeaders() })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },

  documentHubRevokeAcl: function (grantId, callback) {
    if (!_checkAuth(() => services.documentHubRevokeAcl(grantId, callback), callback)) {
      return;
    }
    axios
      .delete("/api/v1/documenthub/acl/" + grantId, { headers: _getHeaders() })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },

  documentHubListByEntity: function (entityType, entityId, callback) {
    if (!_checkAuth(() => services.documentHubListByEntity(entityType, entityId, callback), callback)) {
      return;
    }
    axios
      .get("/api/v1/documenthub/documents", {
        headers: _getHeaders(),
        params: { entityType: entityType, entityId: entityId },
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },

  documentHubListVersions: function (documentUuid, callback) {
    if (!_checkAuth(() => services.documentHubListVersions(documentUuid, callback), callback)) {
      return;
    }
    axios
      .get("/api/v1/documenthub/documents/" + documentUuid + "/versions", { headers: _getHeaders() })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },

  documentHubListAudit: function (documentUuid, params, callback) {
    if (!_checkAuth(() => services.documentHubListAudit(documentUuid, params, callback), callback)) {
      return;
    }
    axios
      .get("/api/v1/documenthub/documents/" + documentUuid + "/audit", { headers: _getHeaders(), params: params || {} })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },

  documentHubAddRelationship: function (documentUuid, payload, callback) {
    if (!_checkAuth(() => services.documentHubAddRelationship(documentUuid, payload, callback), callback)) {
      return;
    }
    axios
      .post("/api/v1/documenthub/documents/" + documentUuid + "/relationships", payload, { headers: _getHeaders() })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },

  documentHubDeleteRelationship: function (documentUuid, relationshipId, callback) {
    if (!_checkAuth(() => services.documentHubDeleteRelationship(documentUuid, relationshipId, callback), callback)) {
      return;
    }
    axios
      .delete("/api/v1/documenthub/documents/" + documentUuid + "/relationships/" + relationshipId, { headers: _getHeaders() })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },

  documentHubDeleteDocument: function (documentUuid, callback) {
    if (!_checkAuth(() => services.documentHubDeleteDocument(documentUuid, callback), callback)) {
      return;
    }
    axios
      .delete("/api/v1/documenthub/documents/" + documentUuid, { headers: _getHeaders() })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },

  documentHubDownload: function (documentUuid, filename, callback) {
    if (!_checkAuth(() => services.documentHubDownload(documentUuid, filename, callback), callback)) {
      return;
    }
    axios
      .get("/api/v1/documenthub/documents/" + documentUuid + "/download", {
        headers: _getHeaders(),
        params: { presign: true },
      })
      .then(function (response) {
        const redirectUrl = response.data && response.data.url;
        if (!redirectUrl) {
          if (callback) callback(false, response);
          return;
        }
        // Top-level navigation to presigned S3 URL — not an XHR follow, so no CORS block.
        window.location.assign(redirectUrl);
        if (callback) callback(true);
      })
      .catch(function (error) {
        if (callback) callback(false, error.response);
      });
  },

  documentHubViewPresign: function (documentUuid, callback) {
    if (!_checkAuth(() => services.documentHubViewPresign(documentUuid, callback), callback)) {
      return;
    }
    axios
      .get("/api/v1/documenthub/documents/" + documentUuid + "/view", {
        headers: _getHeaders(),
        params: { presign: true },
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },

  documentHubViewPageUrl: function (documentUuid) {
    const base = window.location.href.split("#")[0];
    return base + "#/documents/" + documentUuid + "/view";
  },

  documentHubOpenViewInNewTab: function (documentUuid) {
    window.open(services.documentHubViewPageUrl(documentUuid), "_blank", "noopener,noreferrer");
  },

  /** Copy relationships from uploaded doc to target, then delete uploaded doc. */
  documentHubLinkUploadToExisting: function (uploadedDocumentUuid, targetDocumentUuid, callback) {
    if (!_checkAuth(() => services.documentHubLinkUploadToExisting(uploadedDocumentUuid, targetDocumentUuid, callback), callback)) {
      return;
    }
    services.documentHubGetDocument(uploadedDocumentUuid, function (ok, uploaded) {
      if (!ok || !uploaded) {
        callback(false);
        return;
      }
      const relationships = uploaded.relationships || [];
      let index = 0;
      const copyNext = function () {
        if (index >= relationships.length) {
          services.documentHubDeleteDocument(uploadedDocumentUuid, callback);
          return;
        }
        const rel = relationships[index];
        index += 1;
        services.documentHubAddRelationship(
          targetDocumentUuid,
          {
            entityType: rel.entityType,
            entityId: rel.entityId,
            entityRef: rel.entityRef,
            role: rel.role,
            isPrimary: rel.isPrimary,
          },
          function () {
            copyNext();
          }
        );
      };
      copyNext();
    });
  },

  documentHubGraph: function (params, callback) {
    if (!_checkAuth(() => services.documentHubGraph(params, callback), callback)) {
      return;
    }
    axios
      .get("/api/v1/documenthub/graph", { params: params, headers: _getHeaders() })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },

  documentHubSimilarDocuments: function (documentUuid, callback) {
    if (!_checkAuth(() => services.documentHubSimilarDocuments(documentUuid, callback), callback)) {
      return;
    }
    axios
      .get("/api/v1/documenthub/documents/" + documentUuid + "/similar", { headers: _getHeaders() })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        callback(false, error.response);
      });
  },

  documentHubPollFinalizeUntilDone: function (documentUuid, versionUuid, onProgress, callback, payload) {
    const startedAt = Date.now();
    const maxWaitMs = 90000;
    const formatRejection = function (reason) {
      if (reason === "scan_failed") {
        return "Malware scan failed on the uploaded file. Check GuardDuty Malware Protection on the staging bucket (mam-document-store-staging-test in dev/test).";
      }
      if (reason === "malware_detected") {
        return "Upload rejected: malware detected by GuardDuty scan.";
      }
      if (reason === "scan_unsupported") {
        return "Upload rejected: file type is not supported by malware scanning.";
      }
      if (reason === "policy_mismatch") {
        return "Upload rejected: file type or size does not match the document class policy (check file extension and declared size).";
      }
      return "Upload rejected: " + (reason || "unknown reason");
    };
    const poll = function () {
      if (Date.now() - startedAt > maxWaitMs) {
        callback(false, {
          notificationMsg:
            "Upload timed out while finalizing. GuardDuty may still be scanning — wait and retry, or check Malware Protection on the staging bucket. See backend logs for Document promotion errors.",
        });
        return;
      }
      services.documentHubFinalizeVersion(documentUuid, versionUuid, payload || null, function (ok, result) {
        if (!ok) {
          callback(false, result);
          return;
        }
        const status = result.status;
        const data = result.data || {};
        if (status === 409 && data.phase === "duplicate_detected") {
          callback(true, { phase: "duplicate_detected", data: data, documentUuid: documentUuid });
          return;
        }
        if (status === 200 && data.versionStatus === "REJECTED") {
          callback(false, { notificationMsg: formatRejection(data.rejectionReason), data: data });
          return;
        }
        if (status === 202 || status === 503) {
          if (onProgress) {
            onProgress(data.phase || "processing", data);
          }
          const delay = (data.retryAfterSeconds || 2) * 1000;
          setTimeout(poll, delay);
          return;
        }
        callback(true, { phase: "complete", data: data, documentUuid: documentUuid });
      });
    };
    poll();
  },

  supersetGuestToken: function (dashboardUuid, callback) {
    if (!_checkAuth(() => services.supersetGuestToken(dashboardUuid, callback), callback)) {
      return;
    }
    axios
      .get("/api/v1/superset/guest-token/" + encodeURIComponent(dashboardUuid), {
        headers: _getHeaders(),
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },

  supersetCookie: function (callback) {
    if (!_checkAuth(() => services.supersetCookie(callback), callback)) {
      return;
    }
    axios
      .get("/api/v1/superset/cookie", {
        headers: _getHeaders(),
        withCredentials: true,
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        console.error(error);
        callback(false, error.response);
      });
  },

  /**
   * On 401 (missing/expired Superset session), refresh cookie once and retry (GMH pattern).
   */
  _supersetRetryAfterUnauthorized: function (error, retried, retryFn, callback) {
    if (error && error.response && error.response.status === 401 && !retried) {
      services.supersetCookie(function (cookieOk) {
        if (!cookieOk) {
          callback(false, error.response);
          return;
        }
        retryFn(true);
      });
      return true;
    }
    return false;
  },

  /**
   * Clears the Superset session cookie and logs out of Superset server-side.
   * Auth.logout also calls DELETE /api/v1/superset/cookie directly (avoids circular import).
   */
  supersetDeleteCookie: function (callback) {
    if (!_checkAuth(() => services.supersetDeleteCookie(callback), callback)) {
      return;
    }
    axios
      .delete("/api/v1/superset/cookie", {
        headers: _getHeaders(),
        withCredentials: true,
      })
      .then(function (response) {
        if (callback) {
          callback(true, response.data);
        }
      })
      .catch(function (error) {
        console.error(error);
        if (callback) {
          callback(false, error.response);
        }
      });
  },

  supersetDashboards: function (callback, retried) {
    if (!_checkAuth(() => services.supersetDashboards(callback, retried), callback)) {
      return;
    }
    axios
      .get("/api/v1/superset/dashboards", {
        headers: _getHeaders(),
        withCredentials: true,
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        if (
          services._supersetRetryAfterUnauthorized(error, retried, function (didRetry) {
            services.supersetDashboards(callback, didRetry);
          }, callback)
        ) {
          return;
        }
        console.error(error);
        callback(false, error.response);
      });
  },

  supersetEnableEmbed: function (dashboardId, callback, retried) {
    if (!_checkAuth(() => services.supersetEnableEmbed(dashboardId, callback, retried), callback)) {
      return;
    }
    axios
      .put("/api/v1/superset/dashboards/" + encodeURIComponent(dashboardId) + "/embed", {}, {
        headers: _getHeaders(),
        withCredentials: true,
      })
      .then(function (response) {
        callback(true, response.data);
      })
      .catch(function (error) {
        if (
          services._supersetRetryAfterUnauthorized(error, retried, function (didRetry) {
            services.supersetEnableEmbed(dashboardId, callback, didRetry);
          }, callback)
        ) {
          return;
        }
        console.error(error);
        callback(false, error.response);
      });
  },
};

export default services;

